/**
 * PublerClientWizard — Onboarding visual para conectar redes sociales de clientes
 * Flujo: Crear perfil Publer → Invitar cliente → Cliente conecta sus redes → Listo
 * Beautiful, animated, professional. Sin APIs ni aprobaciones de Meta/TikTok.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, Copy, ExternalLink, RefreshCw, Plus,
  Instagram, Globe, Zap, Users, ArrowRight, ChevronDown,
  Play, Check, X, Sparkles, AlertCircle
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NETWORKS = [
  { id: 'instagram', label: 'Instagram',  flag: '📸', color: 'text-pink-400',  bg: 'bg-pink-500/10' },
  { id: 'tiktok',   label: 'TikTok',     flag: '🎵', color: 'text-slate-300', bg: 'bg-slate-500/10' },
  { id: 'facebook', label: 'Facebook',   flag: '📘', color: 'text-blue-400',  bg: 'bg-blue-500/10' },
  { id: 'linkedin', label: 'LinkedIn',   flag: '💼', color: 'text-sky-400',   bg: 'bg-sky-500/10' },
  { id: 'youtube',  label: 'YouTube',    flag: '▶️', color: 'text-red-400',   bg: 'bg-red-500/10' },
];

const HOW_IT_WORKS_STEPS = [
  {
    n: 1,
    icon: '🏢',
    title: 'Tú tienes UNA cuenta Publer (agencia)',
    desc: 'Con tu API Key de Publer, Aynara puede publicar en TODAS las redes de TODOS tus clientes desde un solo lugar.',
    color: 'border-primary/30 bg-primary/5',
  },
  {
    n: 2,
    icon: '👤',
    title: 'Creas un perfil por cada cliente',
    desc: 'Dentro de Publer, creas un "perfil" o "workspace" para cada cliente. Ej: "Laura Marín", "FisioMedic", "Clínica Dental Lima".',
    color: 'border-blue-500/30 bg-blue-500/5',
  },
  {
    n: 3,
    icon: '🔗',
    title: 'El cliente conecta sus redes en 2 minutos',
    desc: 'Le envías un enlace de Publer. El cliente hace clic en "Conectar Instagram/TikTok/Facebook" y autoriza. Publer ya tiene aprobación de Meta y TikTok — no necesitas nada más.',
    color: 'border-emerald-500/30 bg-emerald-500/5',
  },
  {
    n: 4,
    icon: '🤖',
    title: 'Aynara publica automáticamente',
    desc: 'Con el perfil conectado, Aynara usa tu API Key para publicar en los perfiles de cualquier cliente. Sin contraseñas, sin apps adicionales.',
    color: 'border-purple-500/30 bg-purple-500/5',
  },
];

/* ── Interactive Tutorial ──────────────────────────────────────────────────── */
function InteractiveTutorial() {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);

  const TUTORIAL_STEPS = [
    {
      title: '1. Ve a publer.com e inicia sesión',
      action: '→ En el menú lateral, haz clic en "Workspaces"',
      visual: (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[#1a1a2e] border border-white/10">
          <div className="w-8 h-8 rounded-lg bg-[#4361EE] flex items-center justify-center text-white font-bold text-sm">P</div>
          <div>
            <div className="h-2 w-32 bg-white/20 rounded mb-1.5" />
            <div className="h-2 w-20 bg-white/10 rounded" />
          </div>
          <div className="ml-auto">
            <div className="px-3 py-1 rounded-full bg-[#4361EE]/20 text-[#4361EE] text-xs">Workspaces ✓</div>
          </div>
        </div>
      ),
    },
    {
      title: '2. Crea un workspace por cliente',
      action: '→ "New Workspace" → Escribe el nombre del cliente',
      visual: (
        <div className="p-4 rounded-xl bg-[#1a1a2e] border border-white/10 space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#4361EE] flex items-center justify-center text-white text-xs">+</div>
            <span className="text-white/70 text-sm">New Workspace</span>
          </div>
          <div className="p-2 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-pink-500/30 flex items-center justify-center text-xs">L</div>
              <span className="text-white/80 text-xs">Laura Marín</span>
              <div className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> creado</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '3. Invita al cliente a conectar sus redes',
      action: '→ "Members" → "Invite" → Copia el enlace para el cliente',
      visual: (
        <div className="p-4 rounded-xl bg-[#1a1a2e] border border-white/10 space-y-2">
          <p className="text-[10px] text-white/50">Enlace de invitación</p>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10">
            <span className="text-[11px] text-white/60 flex-1 truncate">app.publer.com/invite/laura-marin...</span>
            <button className="text-[#4361EE] text-[11px] flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copiar
            </button>
          </div>
          <p className="text-[10px] text-white/40">El cliente recibe el enlace, hace clic y conecta sus redes sociales en Publer</p>
        </div>
      ),
    },
    {
      title: '4. El cliente conecta Instagram en 30 segundos',
      action: '→ El cliente ve el botón y hace clic. ¡Listo!',
      visual: (
        <div className="p-4 rounded-xl bg-[#1a1a2e] border border-white/10 space-y-3">
          {['📸 Instagram', '🎵 TikTok', '📘 Facebook'].map((n, i) => (
            <div key={n} className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
              <span className="text-sm">{n.split(' ')[0]}</span>
              <span className="text-xs text-white/70 flex-1">{n.split(' ')[1]}</span>
              <div className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${i === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}>
                {i === 0 ? <><Check className="w-3 h-3" /> Conectado</> : 'Conectar'}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: '5. Aynara ya puede publicar por ellos',
      action: '→ ¡Todo listo! Aynara usa tu API Key para publicar en sus cuentas',
      visual: (
        <div className="p-4 rounded-xl bg-[#1a1a2e] border border-emerald-500/30 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary animate-pulse" />
            <span className="text-sm text-emerald-400 font-medium">Aynara publicando para Laura Marín...</span>
          </div>
          <div className="p-2 rounded-lg bg-emerald-500/10 text-xs text-emerald-300">
            "✅ Post programado · Instagram · 15:00 hrs · 🇵🇪 Lima"
          </div>
          <div className="p-2 rounded-lg bg-blue-500/10 text-xs text-blue-300">
            "✅ Reels publicado · TikTok · @lauramarin.negocios"
          </div>
        </div>
      ),
    },
  ];

  const currentStep = TUTORIAL_STEPS[step];

  return (
    <div className="glass-card p-6 space-y-4" data-testid="publer-tutorial">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
            <Play className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h4 className="text-sm font-heading font-semibold text-text-heading">Tutorial interactivo</h4>
            <p className="text-[10px] text-text-muted">Cómo conectar a tus clientes en 5 pasos</p>
          </div>
        </div>
        <div className="text-xs text-text-muted">Paso {step + 1}/{TUTORIAL_STEPS.length}</div>
      </div>

      {/* Progress dots */}
      <div className="flex gap-1.5">
        {TUTORIAL_STEPS.map((_, i) => (
          <button key={i} onClick={() => setStep(i)}
            className={`transition-all rounded-full ${i === step ? 'w-8 h-2 bg-primary' : 'w-2 h-2 bg-white/15 hover:bg-white/30'}`} />
        ))}
      </div>

      {/* Content */}
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium text-text-heading">{currentStep.title}</p>
          <p className="text-xs text-primary mt-0.5">{currentStep.action}</p>
        </div>
        {currentStep.visual}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
          className="text-xs text-text-muted disabled:opacity-30 hover:text-text-heading transition-colors">
          ← Anterior
        </button>
        {step < TUTORIAL_STEPS.length - 1 ? (
          <button onClick={() => setStep(s => s + 1)} className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5">
            Siguiente <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <a href="https://publer.com" target="_blank" rel="noreferrer"
            className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5">
            Ir a Publer <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

/* ── Client Card ──────────────────────────────────────────────────────────── */
function ClientCard({ client, onRefresh }) {
  const connectedNets = client.connected_networks || [];
  const allNets = NETWORKS;

  return (
    <div className="glass-card p-5 space-y-4" data-testid={`publer-client-${client.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center font-bold text-primary text-lg">
            {(client.name || 'C')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-text-heading">{client.name}</p>
            <p className="text-xs text-text-muted">{client.workspace_id ? `Publer ID: ${client.workspace_id}` : 'Sin workspace Publer'}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${connectedNets.length > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-500/15 text-slate-400'}`}>
          {connectedNets.length > 0 ? <><CheckCircle2 className="w-3 h-3" /> {connectedNets.length} redes</> : 'Sin redes'}
        </div>
      </div>

      {/* Networks status */}
      <div className="flex gap-2 flex-wrap">
        {allNets.map(n => {
          const isConnected = connectedNets.includes(n.id);
          return (
            <span key={n.id} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${
              isConnected ? `${n.bg} ${n.color} border-current` : 'bg-white/3 text-text-muted border-white/10'
            }`}>
              {n.flag} {n.label}
              {isConnected && <CheckCircle2 className="w-2.5 h-2.5" />}
            </span>
          );
        })}
      </div>

      {/* Invite link */}
      {client.invite_url && (
        <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/3 border border-border-subtle">
          <span className="text-[10px] text-text-muted flex-1 truncate">{client.invite_url}</span>
          <button onClick={() => { navigator.clipboard.writeText(client.invite_url); toast.success('Enlace copiado'); }}
            className="flex items-center gap-1 text-[10px] text-primary hover:underline whitespace-nowrap">
            <Copy className="w-3 h-3" /> Copiar
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main Wizard ───────────────────────────────────────────────────────────── */
export default function PublerClientWizard({ authFetch }) {
  const [view, setView] = useState('overview'); // overview | clients | tutorial
  const [publerStatus, setPublerStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  useEffect(() => {
    authFetch(`${API}/admin/publer/status`)
      .then(r => r.json())
      .then(j => setPublerStatus(j))
      .catch(() => {});
    // Load connected clients from CRM
    authFetch(`${API}/crm/contacts?limit=20`)
      .then(r => r.json())
      .then(j => {
        const unique = {};
        (j.contacts || []).forEach(c => {
          if (c.nombre_negocio || c.empresa) {
            const key = c.nombre_negocio || c.empresa;
            if (!unique[key]) unique[key] = { id: key, name: key, connected_networks: [], invite_url: null };
          }
        });
        setClients(Object.values(unique).slice(0, 10));
      })
      .catch(() => {});
  }, [authFetch]);

  const connected = publerStatus?.connected;

  return (
    <div className="space-y-6" data-testid="publer-client-wizard">
      {/* Hero header */}
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5" />
        <div className="relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-heading font-bold text-text-heading flex items-center gap-3">
                <span className="text-2xl">📱</span>
                Conexión de Redes Sociales
              </h2>
              <p className="text-text-muted text-sm mt-1">
                Aynara publica en Instagram, TikTok, Facebook y LinkedIn de tus clientes.
                <strong className="text-primary"> Sin APIs. Sin aprobaciones. En 2 minutos.</strong>
              </p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${connected ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-slate-500/10 border-slate-500/20'}`}>
              {connected ? (
                <><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-xs text-emerald-400 font-medium">Publer conectado</span></>
              ) : (
                <><AlertCircle className="w-4 h-4 text-amber-400" /><span className="text-xs text-amber-400 font-medium">Publer sin configurar</span></>
              )}
            </div>
          </div>

          {!connected && (
            <div className="mt-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm font-medium text-amber-300 mb-2">Paso 1: Conecta Publer (solo una vez)</p>
              <div className="flex flex-wrap gap-2">
                <a href="https://publer.com/settings/api" target="_blank" rel="noreferrer"
                  className="btn-primary text-xs py-2 px-4 flex items-center gap-2">
                  <ExternalLink className="w-3.5 h-3.5" /> Obtener API Key en Publer
                </a>
                <button onClick={() => setView('simple_connections')}
                  className="btn-secondary text-xs py-2 px-4 flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5" /> Pegar API Key aquí
                </button>
              </div>
              <p className="text-[10px] text-text-muted mt-2">Ve a Configuración de Admin → Conexiones → Conectar Publer</p>
            </div>
          )}
        </div>
      </div>

      {/* How it works */}
      <div className="glass-card overflow-hidden">
        <button
          onClick={() => setShowHowItWorks(v => !v)}
          className="w-full flex items-center justify-between p-5 text-left"
        >
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-text-heading">¿Cómo funciona? — El flujo completo explicado</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${showHowItWorks ? 'rotate-180' : ''}`} />
        </button>

        {showHowItWorks && (
          <div className="px-5 pb-5 space-y-3">
            {HOW_IT_WORKS_STEPS.map(s => (
              <div key={s.n} className={`flex gap-4 p-4 rounded-2xl border ${s.color}`}>
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-2xl">
                  {s.icon}
                </div>
                <div>
                  <p className="text-sm font-medium text-text-heading">{s.title}</p>
                  <p className="text-xs text-text-muted mt-1 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
            <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20 text-center">
              <p className="text-sm text-primary font-medium">
                💡 Publer ya tiene aprobación de Meta y TikTok. Tus clientes solo hacen clic en "Conectar" y listo.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Interactive Tutorial */}
      <InteractiveTutorial />

      {/* Client list */}
      {clients.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-text-heading">Clientes del CRM</h3>
            <span className="text-xs text-text-muted">{clients.length} clientes encontrados</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {clients.map(c => (
              <ClientCard key={c.id} client={c} />
            ))}
          </div>
        </div>
      )}

      {/* Quick start guide */}
      <div className="glass-card p-5 border border-primary/10">
        <h3 className="text-sm font-medium text-text-heading mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> Guía de 4 pasos para tu primer cliente
        </h3>
        <div className="space-y-3">
          {[
            { step: 1, text: 'Ve a publer.com y crea tu cuenta como agencia (plan Team o Agency)', done: connected },
            { step: 2, text: 'Crea un "Workspace" para el cliente: nombre + logo + redes objetivo', done: false },
            { step: 3, text: 'Invita al cliente con el enlace de Publer — él conecta sus redes (2 min)', done: false },
            { step: 4, text: 'Copia tu API Key de Publer y pégala en Configuración → Conexiones → Publer', done: connected },
          ].map(s => (
            <div key={s.step} className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                s.done ? 'bg-emerald-500/20 text-emerald-400' : 'bg-primary/15 text-primary'
              }`}>
                {s.done ? <Check className="w-3 h-3" /> : s.step}
              </div>
              <p className={`text-sm ${s.done ? 'text-emerald-400 line-through opacity-60' : 'text-text-body'}`}>{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
